import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskedLines } from "./Reveal";

const BOOK_CALL_URL = "https://calendly.com/anchorsideglobal-support/30min";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = ({ scrollToId }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-16%"]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-[62vh] md:min-h-screen flex items-start md:items-end overflow-hidden pt-28 md:pt-32 pb-10 md:pb-14"
      data-testid="hero-section"
    >
      {/* Parallax abstract background, right side — desktop/tablet only */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="hidden md:block absolute top-0 right-0 h-full md:w-[46%] z-0"
      >
        <div className="relative h-full w-full">
          <img
            src="https://images.pexels.com/photos/19553078/pexels-photo-19553078.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-parchment via-parchment/60 to-parchment/10" />
        </div>
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full"
      >
        <h1 className="font-serif text-petrol text-[3rem] leading-[0.95] sm:text-6xl md:text-7xl lg:text-[5.6rem] tracking-tight max-w-5xl">
          <MaskedLines
            delay={0.35}
            lines={["Dedicated offshore teams", "for UK financial advisers."]}
            lineClassName="font-normal"
          />
        </h1>

        <motion.ul
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.12, delayChildren: 0.7 } },
          }}
          className="mt-8 space-y-4 max-w-xl"
        >
          {[
            "Specialised back-office talent & paraplanners",
            "Trained, managed, and built around your practice",
            "Reliable and built to last at a fraction of UK hiring costs",
          ].map((point) => (
            <motion.li
              key={point}
              variants={{
                hidden: { opacity: 0, y: 16 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
              }}
              className="flex items-start gap-3"
            >
              <span className="font-sans text-verdigris text-lg md:text-xl leading-relaxed">–</span>
              <span className="font-sans text-petrol/75 text-lg md:text-xl leading-relaxed">
                {point}
              </span>
            </motion.li>
          ))}
        </motion.ul>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
          className="flex flex-col sm:flex-row gap-4 mt-10"
        >
          <a
            href={BOOK_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-call-btn"
            className="group inline-flex items-center justify-center gap-2 bg-petrol text-parchment px-9 py-4 font-sans text-sm uppercase tracking-[0.16em] hover:bg-petrol/90 transition-colors"
          >
            Book a Call
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <button
            onClick={() => scrollToId("services")}
            data-testid="hero-explore-services-btn"
            className="group inline-flex items-center justify-center gap-2 border border-petrol text-petrol px-9 py-4 font-sans text-sm uppercase tracking-[0.16em] hover:bg-petrol/5 transition-colors"
          >
            Explore Services
            <ArrowDownRight size={18} className="group-hover:translate-y-0.5 transition-transform" />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
